// AstriaMexicoWizard.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { useMexicoColors } from "./AstriaMexicoThemeContext";

// ─── Astria Mexico — input primitives ───────────────────────────────────────
// Clean-outline text inputs + a two-person DOB mini-form. The backend
// (helper/astriaMexicoService.js -> parseEnergyMatchPartnersMX) extracts
// birth details out of the free-text chat message via regex — there is no
// structured payload field for it — so this form's only job is to collect
// the details with a nicer UI than raw typing, then serialize them into a
// plain-Spanish sentence the existing backend parser already understands.

export const MXTextInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}> = ({ value, onChange, placeholder, type = "text" }) => {
  const { colors } = useMexicoColors();
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "14px 16px",
        fontSize: 15,
        borderRadius: 12,
        border: `1.5px solid ${colors.cardBorder}`,
        background: colors.card,
        color: colors.titleColor,
        outline: "none",
        fontFamily: "inherit",
        transition: "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease",
      }}
    />
  );
};

export interface PersonDetails {
  name: string;
  dob: string;
  time: string;
  place: string;
}

export const EMPTY_PERSON_DETAILS: PersonDetails = { name: "", dob: "", time: "", place: "" };

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors } = useMexicoColors();
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: colors.mutedColor,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
};

// One person's birth-detail block (name is optional for "tú", required for
// "tu pareja" so the composed sentence reads naturally either way).
export const MXPersonFields: React.FC<{
  heading: string;
  values: PersonDetails;
  onChange: (values: PersonDetails) => void;
  namePlaceholder?: string;
}> = ({ heading, values, onChange, namePlaceholder }) => {
  const { t } = useTranslation();
  return (
  <div style={{ marginBottom: 20 }}>
    <FieldLabel>{heading}</FieldLabel>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <MXTextInput
        value={values.name}
        onChange={(name) => onChange({ ...values, name })}
        placeholder={namePlaceholder ?? t("mx.common.theirName")}
      />
      <MXTextInput
        value={values.dob}
        onChange={(dob) => onChange({ ...values, dob })}
        placeholder={t("mx.common.dob")}
      />
      <MXTextInput
        value={values.time}
        onChange={(time) => onChange({ ...values, time })}
        placeholder={t("mx.common.time")}
      />
      <MXTextInput
        value={values.place}
        onChange={(place) => onChange({ ...values, place })}
        placeholder={t("mx.common.place")}
      />
    </div>
  </div>
  );
};

export const MXSubmitButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}> = ({ onClick, disabled = false, label }) => {
  const { t } = useTranslation();
  const { colors } = useMexicoColors();
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center gap-1.5 rounded-full text-sm font-semibold transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
      style={{
        width: "100%",
        paddingBlock: 13,
        color: colors.white,
        background: colors.accent,
      }}
    >
      {label ?? t("mx.common.revealReading")}
    </button>
  );
};
